
import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import { onboardingApi } from '@/api/onboarding'
import { 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  FileText, 
  Video, 
  Users, 
  Monitor, 
  Calendar,
  Upload,
  Download,
  Mail,
  Phone,
  User,
  BookOpen,
  Shield,
  Coffee,
  Award,
  ArrowRight,
  Play,
  ChevronRight,
  LucideIcon
} from 'lucide-react'

interface LocalOnboardingStep {
  id: string
  title: string
  description: string
  type: 'document' | 'training' | 'meeting' | 'system_access' | 'equipment' | 'video' | 'welcome'
  status: 'pending' | 'in_progress' | 'completed' | 'overdue'
  dueDate: string
  completedDate?: string
  estimatedTime: string
  required: boolean
  icon: LucideIcon
  actionText: string
  details?: string
}

// Map OnboardingStep to LocalOnboardingStep with UI-specific data
const mapToLocalStep = (step: any, index: number): LocalOnboardingStep => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'documentation': return FileText
      case 'training': return BookOpen
      case 'meeting': return Users
      case 'system': return Monitor
      default: return Video
    }
  }
  
  const getActionText = (category: string, status: string) => {
    if (status === 'completed') return 'Ver detalhes'
    switch (category) {
      case 'documentation': return 'Enviar documentos'
      case 'training': return 'Iniciar treinamento'
      case 'meeting': return 'Ver agenda'
      case 'system': return 'Configurar sistemas'
      default: return 'Começar'
    }
  }
  
  const getLocalType = (category: string) => {
    switch (category) {
      case 'documentation': return 'document'
      case 'training': return 'training'
      case 'meeting': return 'meeting'
      case 'system': return 'system_access'
      default: return 'welcome'
    }
  }
  
  const baseDate = new Date()
  baseDate.setDate(baseDate.getDate() + index)
  
  return {
    id: step.id,
    title: step.title,
    description: step.description,
    type: getLocalType(step.category),
    status: step.status,
    dueDate: baseDate.toISOString().split('T')[0],
    completedDate: step.status === 'completed' ? baseDate.toISOString().split('T')[0] : undefined,
    estimatedTime: step.duration || '30 min',
    required: step.priority === 'high',
    icon: getCategoryIcon(step.category),
    actionText: getActionText(step.category, step.status),
    details: `${step.description}${step.resources ? ` - Recursos: ${step.resources.join(', ')}` : ''}`
  }
}

export default function MyOnboardingPage() {
  const { user, logout } = useAuth()
  usePageTitle('Meu Onboarding')
  const [selectedStep, setSelectedStep] = useState<LocalOnboardingStep | null>(null)

  // Loading states
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Data state
  const [onboardingSteps, setOnboardingSteps] = useState<LocalOnboardingStep[]>([])

  // Load onboarding steps asynchronously
  useEffect(() => {
    const loadOnboardingSteps = async () => {
      try {
        setLoading(true)
        setError(null)

        const roleName = user?.employee?.currentEngagement?.roleName || 'default'

        const steps = await onboardingApi.getOnboardingSteps(roleName)
        const mappedSteps = steps.map(mapToLocalStep)
        setOnboardingSteps(mappedSteps)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load onboarding steps')
        console.error('Error loading onboarding steps:', err)
      } finally {
        setLoading(false)
      }
    }

    loadOnboardingSteps()
  }, [user])

  // Check user authorization first
  if (!user) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg">Acesso não permitido</p>
        </div>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark">
        <div className="bg-brand-dark-light/95 backdrop-blur-sm border-b border-gray-700/50 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-primary/5 to-transparent opacity-50" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center shadow-monumenta-brand">
                  <User className="w-6 h-6 text-brand-dark" />
                </div>
                <div>
                  <p className="font-display text-2xl font-bold text-white mb-1 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
                    Bem-vindo, {user.name}!
                  </p>
                  <p className="text-brand-gray-light font-body">
                    {user.employee?.currentEngagement?.roleName || "N/A"} • {user.employee?.currentEngagement?.departmentName || "N/A"}
                  </p>
                </div>
              </div>
              <button onClick={logout} className="btn-ghost text-sm">
                Sair
              </button>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mb-4"></div>
            <p className="text-white text-lg">Carregando suas etapas de onboarding...</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-brand-dark">
        <div className="bg-brand-dark-light/95 backdrop-blur-sm border-b border-gray-700/50 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-primary/5 to-transparent opacity-50" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center shadow-monumenta-brand">
                  <User className="w-6 h-6 text-brand-dark" />
                </div>
                <div>
                  <p className="font-display text-2xl font-bold text-white mb-1 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
                    Bem-vindo, {user.name}!
                  </p>
                  <p className="text-brand-gray-light font-body">
                    {user.employee?.currentEngagement?.roleName || "N/A"} • {user.employee?.currentEngagement?.departmentName || "N/A"}
                  </p>
                </div>
              </div>
              <button onClick={logout} className="btn-ghost text-sm">
                Sair
              </button>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="text-red-400 text-lg mb-2">Erro ao carregar dados</div>
            <p className="text-brand-gray mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    )
  }

  const completedSteps = onboardingSteps.filter(step => step.status === 'completed').length
  const totalSteps = onboardingSteps.length
  const progress = Math.round((completedSteps / totalSteps) * 100)

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-brand-primary" />
      case 'in_progress':
        return <Clock className="w-5 h-5 text-brand-blue" />
      case 'overdue':
        return <AlertTriangle className="w-5 h-5 text-red-400" />
      default:
        return <Clock className="w-5 h-5 text-brand-gray" />
    }
  }

  const getStatusBadge = (status: string, required: boolean) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-display font-medium border"
    
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-brand-primary bg-opacity-20 text-brand-primary border-brand-primary border-opacity-40`
      case 'in_progress':
        return `${baseClasses} bg-brand-blue bg-opacity-20 text-brand-blue border-brand-blue border-opacity-40`
      case 'overdue':
        return `${baseClasses} bg-red-400 bg-opacity-20 text-red-400 border-red-400 border-opacity-40`
      default:
        return required 
          ? `${baseClasses} bg-brand-gray bg-opacity-20 text-brand-gray border-brand-gray border-opacity-40`
          : `${baseClasses} bg-brand-gray bg-opacity-10 text-brand-gray border-brand-gray border-opacity-20`
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Concluído'
      case 'in_progress': return 'Em Progresso'
      case 'overdue': return 'Atrasado'
      default: return 'Pendente'
    }
  }

  return (
    <div className="min-h-screen bg-brand-dark">
      {/* Header */}
      <div className="bg-brand-dark-light/95 backdrop-blur-sm border-b border-gray-700/50 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-primary/5 to-transparent opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center shadow-monumenta-brand">
                <User className="w-6 h-6 text-brand-dark" />
              </div>
              <div>
                <p className="font-display text-2xl font-bold text-white mb-1 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
                  Bem-vindo, {user.name}!
                </p>
                <p className="text-brand-gray-light font-body">
                  {user.employee?.currentEngagement?.roleName || "N/A"} • {user.employee?.currentEngagement?.departmentName || "N/A"}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="btn-ghost text-sm"
            >
              Sair
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Overview */}
        <div className="card-hover mb-8 animate-fade-in-up">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-xl font-semibold text-white mb-2">
                Seu Progresso de Integração
              </h2>
              <p className="text-brand-gray-light font-body">
                Você completou {completedSteps} de {totalSteps} etapas ({progress}%)
              </p>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-bold text-brand-primary mb-1">
                {progress}%
              </div>
              <p className="text-sm text-brand-gray font-body">Concluído</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-brand-dark bg-opacity-60 rounded-full h-3 mb-4">
            <div
              className="bg-brand-primary h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Quick Stats */}
          <div className="dashboard-grid md:grid-cols-4">
            <div className="metric-card">
              <div className="metric-value text-brand-primary">
                {onboardingSteps.filter(s => s.status === 'completed').length}
              </div>
              <div className="metric-label">Concluídas</div>
            </div>
            <div className="metric-card">
              <div className="metric-value text-brand-blue">
                {onboardingSteps.filter(s => s.status === 'in_progress').length}
              </div>
              <div className="metric-label">Em Progresso</div>
            </div>
            <div className="metric-card">
              <div className="metric-value text-brand-gray">
                {onboardingSteps.filter(s => s.status === 'pending').length}
              </div>
              <div className="metric-label">Pendentes</div>
            </div>
            <div className="metric-card">
              <div className="metric-value text-white">
                {onboardingSteps.filter(s => s.required).length}
              </div>
              <div className="metric-label">Obrigatórias</div>
            </div>
          </div>
        </div>

        {/* Onboarding Steps */}
        <div className="card-hover animate-fade-in-up">
          <div className="p-6">
            <h3 className="font-display text-lg font-semibold text-white mb-6">
              Suas Próximas Etapas
            </h3>
            
            <div className="space-y-4">
              {onboardingSteps.map((step, index) => {
                const Icon = step.icon
                const isNext = step.status === 'pending' && 
                  onboardingSteps.slice(0, index).every(s => s.status === 'completed' || !s.required)
                
                return (
                  <div
                    key={step.id}
                    className={`border border-brand-gray border-opacity-20 rounded p-4 transition-all duration-300 cursor-pointer ${
                      isNext ? 'border-brand-primary border-opacity-40 bg-brand-primary bg-opacity-5' : 'hover:border-brand-gray hover:border-opacity-40'
                    }`}
                    onClick={() => setSelectedStep(step)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(step.status)}
                          <Icon className="w-6 h-6 text-brand-gray" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-1">
                            <h4 className="font-display font-semibold text-white">
                              {step.title}
                            </h4>
                            {isNext && (
                              <span className="bg-brand-primary text-brand-dark px-2 py-1 rounded text-xs font-display font-medium">
                                Próxima
                              </span>
                            )}
                            {!step.required && (
                              <span className="bg-brand-gray bg-opacity-20 text-brand-gray px-2 py-1 rounded text-xs font-display font-medium">
                                Opcional
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-brand-gray mb-2">
                            {step.description}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-brand-gray">
                            <span>{new Date(step.dueDate).toLocaleDateString('pt-BR')}</span>
                            <span>{step.estimatedTime}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <span className={getStatusBadge(step.status, step.required)}>
                          {getStatusText(step.status)}
                        </span>
                        <ChevronRight className="w-5 h-5 text-brand-gray" />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 card-hover">
          <h3 className="font-display text-lg font-semibold text-white mb-4">
            Precisa de Ajuda?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-brand-dark bg-opacity-50 rounded-monumenta hover:bg-brand-dark-lighter transition-colors duration-200">
              <Mail className="w-5 h-5 text-brand-primary" />
              <div>
                <p className="text-white text-sm font-medium font-display">Email do RH</p>
                <p className="text-brand-gray text-xs font-body">janine@monumenta.com.br</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-brand-dark bg-opacity-50 rounded-monumenta hover:bg-brand-dark-lighter transition-colors duration-200">
              <Phone className="w-5 h-5 text-brand-primary" />
              <div>
                <p className="text-white text-sm font-medium font-display">Telefone</p>
                <p className="text-brand-gray text-xs font-body">(61) 3333-4444</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Detail Modal */}
      {selectedStep && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                  <selectedStep.icon className="w-6 h-6 text-brand-primary" />
                  <h2 className="font-display text-xl font-bold text-white">
                    {selectedStep.title}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedStep(null)}
                  className="btn-icon text-brand-gray hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-display font-semibold text-white mb-2">Descrição</h3>
                  <p className="text-brand-gray">{selectedStep.description}</p>
                </div>
                
                {selectedStep.details && (
                  <div>
                    <h3 className="font-display font-semibold text-white mb-2">Detalhes</h3>
                    <p className="text-brand-gray">{selectedStep.details}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-display font-semibold text-white mb-2">Prazo</h3>
                    <p className="text-brand-gray">{new Date(selectedStep.dueDate).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-white mb-2">Tempo Estimado</h3>
                    <p className="text-brand-gray">{selectedStep.estimatedTime}</p>
                  </div>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button className="btn-primary flex items-center gap-2 group">
                    <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                    {selectedStep.actionText}
                  </button>
                  <button 
                    onClick={() => setSelectedStep(null)}
                    className="btn-ghost"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 